import { Link, useLocation } from "wouter";
import { useBookCta } from "@/hooks/use-book-href";
import { CalendarCheck } from "lucide-react";
import { useEffect, useState } from "react";

const CTA_CLASS =
  "inline-flex items-center justify-center gap-2.5 font-sans font-semibold text-[13px] tracking-[0.08em] uppercase px-8 py-4 rounded-none bg-sun text-shell shadow-[0_4px_20px_rgba(23,49,28,0.35)] hover:bg-sun-hover hover:-translate-y-0.5 transition-all duration-200";

/** Mobile-only sticky booking CTA for conversion */
export function StickyMobileBookCta() {
  const [location] = useLocation();
  const bookCta = useBookCta();
  const isHome = location === "/" || location === "";
  // Hide the pill while an in-page primary booking CTA or the homepage
  // availability section is visible, so guests never see two CTAs stacked.
  const [inPageCtaVisible, setInPageCtaVisible] = useState(false);

  useEffect(() => {
    setInPageCtaVisible(false);
    const targets = [
      ...Array.from(document.querySelectorAll("[data-primary-book-cta]")),
      ...Array.from(document.querySelectorAll("[data-sticky-book-anchor]")),
    ];
    if (targets.length === 0) return;

    const visible = new Set<Element>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        }
        setInPageCtaVisible(visible.size > 0);
      },
      { rootMargin: "0px 0px -8px 0px", threshold: 0.12 },
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [location]);

  // Hide on booking page — full widget already in view
  if (location === "/booking" || location.startsWith("/booking/")) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-transparent pointer-events-none p-4 pb-[max(1rem,env(safe-area-inset-bottom))] transition-opacity duration-300 ${
        inPageCtaVisible ? "opacity-0" : "opacity-100"
      }`}
      role="complementary"
      aria-label="Quick booking"
      aria-hidden={inPageCtaVisible}
    >
      <div className={`flex justify-center ${inPageCtaVisible ? "pointer-events-none" : "pointer-events-auto"}`}>
        {isHome ? (
          <a
            href="#check-availability"
            tabIndex={inPageCtaVisible ? -1 : undefined}
            className={CTA_CLASS}
            data-testid="sticky-mobile-book-cta"
          >
            <CalendarCheck className="h-[18px] w-[18px]" aria-hidden="true" />
            Book Your Stay
          </a>
        ) : (
          <Link
            href={bookCta.href}
            tabIndex={inPageCtaVisible ? -1 : undefined}
            className={CTA_CLASS}
            data-testid="sticky-mobile-book-cta"
          >
            <CalendarCheck className="h-[18px] w-[18px]" aria-hidden="true" />
            {bookCta.choosing ? "Choose a Home" : "Book Your Stay"}
          </Link>
        )}
      </div>
    </div>
  );
}
